import { HomesToRent, ctaVariants } from '@greystar/designsystem';
import {
  getDictionaryEntry,
  useAnalytics,
  getEngine,
  getLinkIcon,
  isValidIconName,
  SEARCHBOX_SUBTITLE_FALLBACK,
} from '@greystar/lib';
import ResponsiveImage from '../../components/ResponsiveImage/ResponsiveImage';
import { SearchBoxCsr } from '../../components/search/search-box/search-box-csr';
import { IntroductionCtaProps } from '../../component-props';
import { Text as JssText, withDatasourceCheck } from '@sitecore-jss/sitecore-jss-nextjs';
import { useI18n } from 'next-localization';
import { useEffect, useState } from 'react';
import {
  SearchBox as HeadlessSearchBox,
  InstantProducts as HeadlessInstantProducts,
  buildStandaloneSearchBox,
  buildInstantProducts,
} from '@coveo/headless/commerce';

/**
 * A variant of the Introduction component that includes a sub-heading.
 *
 * This component renders an introduction section with images, heading, text and sub-heading.
 * It uses the `Intro` component from the design system to structure the layout.
 *
 * @param props - The properties for the IntroductionWithCallToAction component.
 * @param props.fields - The fields provided by the datasource.
 * @param props.fields.heading - The main heading text for the introduction.
 * @param props.fields.text - The body text for the introduction.
 * @param props.fields.image1 - The first image to display in the introduction.
 * @param props.fields.image2 - The second image to display in the introduction.
 * @param props.fields.link - The call-to-action link field.
 *
 * @returns A JSX element rendering the introduction section with a call-to-action.
 */
export const IntroductionCta = (props: IntroductionCtaProps): JSX.Element => {
  const searchBoxId = props.rendering.uid ?? 'searchBoxIntroductionCta';

  const controllerSearchBox: HeadlessSearchBox = buildStandaloneSearchBox(getEngine(), {
    options: {
      redirectionUrl: '/s',
      id: searchBoxId,
    },
  });

  const controllerInstantProducts: HeadlessInstantProducts = buildInstantProducts(getEngine(), {
    options: {
      searchBoxId: searchBoxId,
    },
  });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSearchButton, setIsSearchButton] = useState(false);
  const analytics = useAnalytics();
  const { heading, text, image1, image2, link } = props.fields;

  const ctaVariant = props.params?.ctaVariant;
  const iconList = props.params?.iconList;
  const iconPosition = props.params?.iconPosition;
  const showSearchButton = props.params?.showSearchButton;

  useEffect(() => {
    if (showSearchButton === '1') {
      setIsSearchButton(true);
    } else {
      setIsSearchButton(false);
    }
  }, [showSearchButton]);

  const handleModalClick = () => {
    const eventData = {
      event: 'property_search_open',
      action_type: 'search model open',
      module_name: heading.value,
      inpage_location: 'top',
      click_text: 'Search for homes',
      click_type: 'internal',
      destination_url: link.value.href ?? '',
    };

    analytics.trackEvent('property_search_open', eventData);

    // Toggle modal open/close
    setIsModalOpen((prev) => !prev);
  };

  // Render fields
  const headingField = <JssText field={heading} />;
  const textField = <JssText field={text} />;
  const renderImage1 = <ResponsiveImage image={image1} fill={true} />;
  const renderImage2 = <ResponsiveImage image={image2} fill={true} />;

  // type inference for cta variant
  type CtaVariantKey = keyof typeof ctaVariants;
  const rawKey = (ctaVariant ?? 'secondary') as CtaVariantKey;
  const ctaVariantType = ctaVariants[rawKey] ?? ctaVariants.secondary;

  // type inference + fallback for cta icon position
  let position: 'left' | 'right' = 'left';
  if (iconPosition) {
    position = iconPosition.toLowerCase() === 'left' ? 'left' : 'right';
  }

  const rawIcon = iconList?.replace(/\s+/g, '');
  const ctaIcon = isValidIconName(rawIcon) ? rawIcon : undefined;

  const ctaLink = {
    label: link?.value?.text ?? 'Title not set',
    href: link?.value?.href ?? '/',
    variant: ctaVariantType,
    icon: getLinkIcon({ iconName: ctaIcon, iconPosition: position, variant: ctaVariantType }),
  };

  const ctaSearch = {
    label: link?.value?.text ?? 'Title not set',
    variant: ctaVariantType,
    icon: getLinkIcon({ iconName: ctaIcon, iconPosition: position, variant: ctaVariantType }),
    onClick: handleModalClick,
  };

  const borderStyle = props.params?.borderStyle as 'top' | 'bottom' | undefined | null;

  const { t } = useI18n();

  return (
    <div>
      <HomesToRent
        borderPosition={borderStyle}
        img1={renderImage1}
        img2={renderImage2}
        bodyCopy={textField}
        heading={headingField}
        buttonProps={ctaSearch}
        linkProps={ctaLink}
        ctaType={isSearchButton ? 'button' : 'link'}
      />

      {isModalOpen && (
        <SearchBoxCsr
          variant="modal"
          headerTitle={getDictionaryEntry(t('searchbox_title'), 'Search for homes')}
          headerSubtitle={getDictionaryEntry(t('searchbox_subtitle'), SEARCHBOX_SUBTITLE_FALLBACK)}
          onClose={handleModalClick}
          searchPromptText={getDictionaryEntry(t('searchbox_prompttext'), 'Search for homes')}
          searchResultsTitle=""
          controllerSearchBox={controllerSearchBox}
          controllerInstantProducts={controllerInstantProducts}
        />
      )}
    </div>
  );
};

export default withDatasourceCheck()<IntroductionCtaProps>(IntroductionCta);
